import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Outlet, useLocation, useNavigate, useParams } from 'react-router-dom';
import { MY_RFQ_DRAFTS_KEY, useCreateRFQDraft, useUpdateRFQDraft } from '@steelbuy/data-access';
import { RFQDraftModel } from '@steelbuy/ts-shared';
import {
    Notification,
    NotificationLevel,
    Wizard,
    WizardBar,
    WizardBarItemStatus,
    useWizardStepsContext,
} from '@steelbuy/ui-primitive';
import { isRequestQuotePath } from '@steelbuy/util';
import { getCreateRFQDraftModel, getRFQSteps, getStepRoute, hasFormData, rfqValidationLookup } from './createRFQUtil';
import { useRfqFormContext } from './RfqFormContext';
import { RFQType } from './Schema';
import { RoutePath } from '../../router/Routes';
import { TableLayout } from '../../views/layouts/table-layout/TableLayout';
import { CreateRFQUnsavedChangesModal } from '../create-rfq-unsaved-changes/CreateRFQUnsavedChangesModal';
import { PageHeader } from '../page-header/PageHeader';
import './CreateRFQ.scss';

const validateSteps = (
    updateStepStatus: (step: string, status: WizardBarItemStatus) => void,
    activeStepIndex: number,
    isRequestQuote: boolean,
    formData?: Partial<RFQType>
) => {
    getRFQSteps(isRequestQuote).forEach((step, index) => {
        if (activeStepIndex !== index) {
            updateStepStatus(
                step,
                rfqValidationLookup[step](formData) ? WizardBarItemStatus.SUCCESS : WizardBarItemStatus.INCOMPLETE
            );
        }
    });
};
export const CreateRFQ = () => {
    const { t } = useTranslation('translation');
    const { pathname, state } = useLocation();
    const isRequestQuote = isRequestQuotePath(pathname);
    const { draftId: draftIdParams } = useParams();
    const queryClient = useQueryClient();
    const [createDraftError, setCreateDraftError] = useState('');
    const [createDraftSuccess, setCreateDraftSuccess] = useState(state?.successNotification || '');

    const navigate = useNavigate();

    const { formData, getUpdatedFormValues, draftId, setDraft, goBack, allowNavigation, onSaveDraft } =
        useRfqFormContext();

    const { setActiveStep, stepStatus, reset, updateStepStatus } = useWizardStepsContext();
    let currentStep = 1;
    if (pathname.indexOf('/step') !== -1) {
        currentStep = parseInt(pathname.split('/step')[1], 10);
    }
    if (Number.isNaN(currentStep)) {
        currentStep = 1;
    }
    const createRFQDraftMutation = useCreateRFQDraft();
    const updateRFQDraftMutation = useUpdateRFQDraft();

    const currentStepIndex = currentStep - 1;

    useEffect(() => {
        setActiveStep(getRFQSteps(isRequestQuote)[currentStep - 1]);
    }, [currentStep]);

    useEffect(() => {
        if (!hasFormData(formData) && currentStepIndex !== 0) {
            reset();
            navigate(isRequestQuote ? RoutePath.CREATE_REQUEST_QUOTE_WIZARD : RoutePath.CREATE_RFQ_WIZARD);
        }
    }, []);

    useEffect(() => {
        if (draftId) {
            validateSteps(updateStepStatus, currentStepIndex, isRequestQuote, formData);
        }
    }, [draftId]);

    const handleCreateRFQDraft = () => {
        const rfqData = getUpdatedFormValues();
        const payload = getCreateRFQDraftModel(rfqData);
        const mutateOptions = {
            onSuccess: ({ id }: RFQDraftModel) => {
                queryClient.removeQueries({ queryKey: [MY_RFQ_DRAFTS_KEY] });
                setDraft(rfqData, id);
                validateSteps(updateStepStatus, currentStepIndex, isRequestQuote, rfqData);
                onSaveDraft();
                if (!draftIdParams) {
                    navigate(getStepRoute(currentStep, isRequestQuote, id), {
                        state: {
                            draftRFQ: {
                                ...payload,
                                id,
                            },
                            successNotification: t('application.createDraftRFQ.createDraftRFQSuccess'),
                        },
                    });
                } else {
                    setCreateDraftSuccess(t('application.createDraftRFQ.createDraftRFQSuccess'));
                }
            },
            onError: () => setCreateDraftError(t('application.createDraftRFQ.createDraftRFQFailed')),
        };

        if (!payload) {
            setCreateDraftError(t('application.createDraftRFQ.createDraftRFQFailed'));
            return;
        }
        setCreateDraftError('');
        setCreateDraftSuccess('');
        if (draftId) {
            updateRFQDraftMutation.mutate({ ...payload, id: draftId }, mutateOptions);
        } else {
            createRFQDraftMutation.mutate(payload, mutateOptions);
        }
    };

    const header = draftId ? t('application.createRFQ.headerEditDraft') : t('application.createRFQ.headerCreateNew');

    const pageHeaderProps = useMemo(
        () =>
            goBack?.link && goBack?.label
                ? {
                      onBackClick: () => {
                          navigate(goBack.link);
                      },
                      previousPageTitle: goBack.label,
                  }
                : {},
        [goBack?.link, goBack?.label]
    );

    return (
        <>
            <PageHeader pageTitle={header} {...pageHeaderProps} />

            <div className="create-rfq">
                <Wizard>
                    <TableLayout>
                        {createDraftError && (
                            <Notification
                                level={NotificationLevel.ERROR}
                                message={createDraftError}
                                onClose={() => setCreateDraftError('')}
                                className="create-rfq__save-draft-notification"
                                closeButton
                                stayOpen
                            />
                        )}
                        {createDraftSuccess && (
                            <Notification
                                level={NotificationLevel.SUCCESS}
                                message={createDraftSuccess}
                                onClose={() => setCreateDraftError('')}
                                className="create-rfq__save-draft-notification"
                                closeButton
                            />
                        )}
                        {state?.rejectedRFQId && (
                            <Notification
                                level={NotificationLevel.SUCCESS}
                                message={t('application.myRFQDetails.restartRFQSuccess')}
                                onClose={() => setCreateDraftError('')}
                                className="create-rfq__save-draft-notification"
                                closeButton
                            />
                        )}
                        <WizardBar
                            currentStepIndex={currentStepIndex}
                            items={getRFQSteps(isRequestQuote).map((step, index) => ({
                                label: t(`application.createRFQSteps.labels.${step}`),
                                status: stepStatus[step],
                                selected: index === currentStepIndex,
                                onClick: allowNavigation
                                    ? () => {
                                          if (index !== currentStepIndex) {
                                              getUpdatedFormValues(true);
                                              navigate(getStepRoute(index + 1, isRequestQuote, draftId), {
                                                  replace: true,
                                              });
                                          }
                                      }
                                    : undefined,
                            }))}
                        />

                        <Outlet
                            context={{
                                onSaveDraft: handleCreateRFQDraft,
                                loadingState: createRFQDraftMutation.isLoading || updateRFQDraftMutation.isLoading,
                            }}
                        />
                    </TableLayout>
                </Wizard>
                {!isRequestQuote && (
                    <CreateRFQUnsavedChangesModal
                        onError={() => setCreateDraftError(t('application.createDraftRFQ.createDraftRFQFailed'))}
                    />
                )}
            </div>
        </>
    );
};
